import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';

const RolesDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isNew = !id || id === 'new';

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [permissions, setPermissions] = useState<any[]>([]);
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch Permissions
                const permResponse = await fetch(`${API_URL}/v1/admin/permissions`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (permResponse.ok) {
                    setPermissions(await permResponse.json());
                }

                if (!isNew) {
                    // Fetch Role Details
                    console.log("Fetching role", id);
                    const response = await fetch(`${API_URL}/v1/admin/roles/${id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const roleData = await response.json();
                        setName(roleData.name);
                        setDescription(roleData.description || '');
                        // Map permissions to IDs
                        if (roleData.permissions) {
                            setSelectedPermissions(roleData.permissions.map((p: any) => p.id));
                        }
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            fetchData();
        }
    }, [id, isNew, token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { name, description, permissions: selectedPermissions };
            console.log("Saving role:", payload);

            const url = isNew ? `${API_URL}/v1/admin/roles` : `${API_URL}/v1/admin/roles/${id}`;
            const method = isNew ? 'POST' : 'PUT';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                alert("Role saved successfully");
                navigate('/admin/roles');
            } else {
                alert("Failed to save role");
            }
        } catch (error) {
            console.error("Error saving role:", error);
            alert("Error saving role");
        } finally {
            setLoading(false);
        }
    };

    const togglePermission = (permId: number) => {
        setSelectedPermissions(prev =>
            prev.includes(permId)
                ? prev.filter(id => id !== permId)
                : [...prev, permId]
        );
    };

    return (
        <SiteLayout>
            <Header icon='admin_panel_settings' title={isNew ? 'Nouveau Rôle' : 'Modifier Rôle'} />
            <div style={{ marginBottom: '20px' }}>
                <button className="btn btn-secondary" onClick={() => navigate('/admin/roles')} style={{ marginBottom: '10px' }}>
                    <i className="material-icons" style={{ verticalAlign: 'middle' }}>arrow_back</i> Retour
                </button>
                <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1e293b' }}>
                    {isNew ? 'Nouveau Rôle' : 'Modifier Rôle'}
                </h2>
            </div>

            <div className="box" style={{ padding: '30px' }}>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Nom du Rôle</label>
                        <input
                            type="text"
                            className="form-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            placeholder="Ex: manager"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            placeholder="Description du rôle..."
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Gestion des Permissions</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '20px', marginTop: '10px' }}>
                            {/* Available Permissions */}
                            <div>
                                <div style={{ fontWeight: '600', marginBottom: '10px', color: '#475569' }}>
                                    <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '5px' }}>lock_open</i>
                                    Permissions Disponibles ({permissions.filter(p => !selectedPermissions.includes(p.id)).length})
                                </div>
                                <div style={{
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    minHeight: '300px',
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    backgroundColor: '#f8fafc'
                                }}>
                                    {permissions.filter(p => !selectedPermissions.includes(p.id)).map(perm => (
                                        <div
                                            key={perm.id}
                                            style={{
                                                padding: '12px',
                                                borderBottom: '1px solid #e2e8f0',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s',
                                                backgroundColor: '#fff'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                            onClick={() => togglePermission(perm.id)}
                                        >
                                            <div style={{ fontWeight: '500', fontSize: '0.9rem' }}>{perm.key}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                                {perm.description || 'Cliquer pour ajouter'}
                                            </div>
                                        </div>
                                    ))}
                                    {permissions.filter(p => !selectedPermissions.includes(p.id)).length === 0 && (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                                            Toutes les permissions sont assignées
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Control Buttons */}
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px' }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '8px', minWidth: '40px' }}
                                    onClick={() => {
                                        const available = permissions.filter(p => !selectedPermissions.includes(p.id));
                                        if (available.length > 0) {
                                            setSelectedPermissions([...selectedPermissions, ...available.map(p => p.id)]);
                                        }
                                    }}
                                    title="Tout ajouter"
                                >
                                    <i className="material-icons">keyboard_double_arrow_right</i>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '8px', minWidth: '40px' }}
                                    onClick={() => setSelectedPermissions([])}
                                    title="Tout retirer"
                                >
                                    <i className="material-icons">keyboard_double_arrow_left</i>
                                </button>
                            </div>

                            {/* Assigned Permissions */}
                            <div>
                                <div style={{ fontWeight: '600', marginBottom: '10px', color: '#475569' }}>
                                    <i className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '5px' }}>vpn_key</i>
                                    Permissions Assignées ({selectedPermissions.length})
                                </div>
                                <div style={{
                                    border: '2px solid #3b82f6',
                                    borderRadius: '8px',
                                    minHeight: '300px',
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    backgroundColor: '#eff6ff'
                                }}>
                                    {permissions.filter(p => selectedPermissions.includes(p.id)).map(perm => (
                                        <div
                                            key={perm.id}
                                            style={{
                                                padding: '12px',
                                                borderBottom: '1px solid #bfdbfe',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s',
                                                backgroundColor: '#fff'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                            onClick={() => togglePermission(perm.id)}
                                        >
                                            <div style={{ fontWeight: '500', fontSize: '0.9rem', color: '#1e40af' }}>
                                                <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '5px' }}>check_circle</i>
                                                {perm.key}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', marginLeft: '21px' }}>
                                                {perm.description || 'Cliquer pour retirer'}
                                            </div>
                                        </div>
                                    ))}
                                    {selectedPermissions.length === 0 && (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                                            Aucune permission assignée
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>
                            💡 Cliquez sur une permission pour la déplacer entre les listes
                        </div>
                    </div>

                    <div style={{ marginTop: '30px', display: 'flex', gap: '10px' }}>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/roles')}>
                            Annuler
                        </button>
                    </div>
                </form>
            </div>
        </SiteLayout>
    );
};

export default RolesDetails;
